import React, { memo, useState } from "react";
import { cn } from "../../../../../services/common/className";
import Tag from "ui-components/molecules/tag/tag";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import moment from "moment";
import { DispatchBoardQueueLoadDetailItem } from "app/data/city-delivery/dispatch-board/models";
import "./cityDeliveryLoadDetailItem.scss";

export interface CityDeliveryLoadDetailItemProps {
  item: DispatchBoardQueueLoadDetailItem;
}

function formatUpdatedAt(updatedAt: string): string {
  if (!updatedAt) return "";
  const m = moment(updatedAt);
  return m.isValid() ? m.format("MMM D, YYYY h:mm A") : updatedAt;
}

function hasValue(v: string | number | undefined): boolean {
  return v !== undefined && v !== null && v !== "";
}

/** For "Show more" sections: show value or "-" when empty (like picklist details). */
function displayValue(v: string | number | undefined): string {
  if (v === undefined || v === null) return "-";
  if (typeof v === "string" && v.trim() === "") return "-";
  return String(v);
}

const CityDeliveryLoadDetailItem: React.FC<CityDeliveryLoadDetailItemProps> = memo(({ item }) => {
  const [showMore, setShowMore] = useState(false);

  return (
    <div className="city-delivery-load-detail-item">
      <div className="city-delivery-load-detail-item__title">
        <span className="city-delivery-load-detail-item__title__seq-no">{item.itemSequenceNumber}</span>
        {item.itemDesc}
        {item.itemNumberType && (
          <>
            {" – "}
            <span className="city-delivery-load-detail-item--text-light">
              {item.itemNumberType} {item.itemNumber}
            </span>
          </>
        )}
        {item.status != null && String(item.status).trim() !== "" && (
          <Tag mix={cn("city-delivery-load-detail-item__status")("default")}>
            {String(item.status).toUpperCase()}
          </Tag>
        )}
      </div>
      <div className="city-delivery-load-detail-item__base-properties">
        {hasValue(item.location) && (
          <div className="city-delivery-load-detail-item__base-properties__item">
            <span className="city-delivery-load-detail-item--text-light">Location:</span> {item.location}
          </div>
        )}
        {item.status != null && String(item.status).trim() !== "" && (
          <div className="city-delivery-load-detail-item__base-properties__item">
            <span className="city-delivery-load-detail-item--text-light">Status:</span> {String(item.status)}
          </div>
        )}
        {hasValue(item.pieces) && (
          <div className="city-delivery-load-detail-item__base-properties__item">
            <span className="city-delivery-load-detail-item--text-light">Pieces:</span> {item.pieces}
          </div>
        )}
        {hasValue(item.weight) && (
          <div className="city-delivery-load-detail-item__base-properties__item">
            <span className="city-delivery-load-detail-item--text-light">Weight:</span> {item.weight} lb
          </div>
        )}
        {hasValue(item.updatedAt) && (
          <div className="city-delivery-load-detail-item__base-properties__item">
            <span className="city-delivery-load-detail-item--text-light">Last changed at:</span>{" "}
            {formatUpdatedAt(item.updatedAt)}
          </div>
        )}
        {hasValue(item.updatedBy) && (
          <div className="city-delivery-load-detail-item__base-properties__item">
            <span className="city-delivery-load-detail-item--text-light">Last changed by:</span> {item.updatedBy}
          </div>
        )}
      </div>
      <div
        className="city-delivery-load-detail-item__show-details"
        onClick={() => setShowMore(!showMore)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setShowMore(!showMore)}
      >
        Show {showMore ? "less" : "more"}
      </div>
      {showMore && (
        <ul className="city-delivery-load-detail-item__additional-details">
          <li className="city-delivery-load-detail-item__additional-details__ele">
            <div className="city-delivery-load-detail-item__additional-details__ele__title">
              <XGSIcon
                className="city-delivery-load-detail-item__additional-details__ele__title__icon"
                icon={XGSIcons.faCircle}
              />
              Item Identifiers
            </div>
            <div className="city-delivery-load-detail-item__additional-details__ele__content">
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Item Sequence Number:</span> {displayValue(item.itemSequenceNumber)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Item Number:</span> {displayValue(item.itemNumber)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Item Number Type:</span> {displayValue(item.itemNumberType)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">SKU:</span> {displayValue(item.sku)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Roll Number:</span> {displayValue(item.rollNumber)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Serial Number:</span> {displayValue(item.serialNumber)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Item Description:</span> {displayValue(item.itemDesc)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Commodity Code:</span> {displayValue(item.commodityCode)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Color:</span> {displayValue(item.color)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Style:</span> {displayValue(item.style)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Dyelot:</span> {displayValue(item.dyelot)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">PO #:</span> {displayValue(item.itemPoNumber)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Part Number:</span> {displayValue(item.partNumber)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Sidemark:</span> {displayValue(item.sidemark)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Location:</span> {displayValue(item.location)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Status:</span> {item.status != null ? String(item.status) : "-"}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Last Changed at:</span> {hasValue(item.updatedAt) ? formatUpdatedAt(item.updatedAt) : "-"}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Last Changed by:</span> {displayValue(item.updatedBy)}
              </div>
            </div>
          </li>
          <li className="city-delivery-load-detail-item__additional-details__ele">
            <div className="city-delivery-load-detail-item__additional-details__ele__title">
              <XGSIcon
                className="city-delivery-load-detail-item__additional-details__ele__title__icon"
                icon={XGSIcons.faCircle}
              />
              Physical Properties
            </div>
            <div className="city-delivery-load-detail-item__additional-details__ele__content">
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Type:</span> {displayValue(item.type)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Pieces:</span> {displayValue(item.pieces)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Weight:</span> {displayValue(item.weight)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Square Yards:</span> {displayValue(item.squareYards)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Size:</span> {displayValue(item.size)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Length:</span> {displayValue(item.length)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Width:</span> {displayValue(item.width)}
              </div>
              <div className="city-delivery-load-detail-item__additional-details__ele__content__pair">
                <span className="city-delivery-load-detail-item--text-light">Height:</span> {displayValue(item.height)}
              </div>
            </div>
          </li>
        </ul>
      )}
    </div>
  );
});

CityDeliveryLoadDetailItem.displayName = "CityDeliveryLoadDetailItem";

export default CityDeliveryLoadDetailItem;
